import { createFeatureSelector, createSelector } from '@ngrx/store';

import { getNodeValue, hasNode } from '../utils/ory.helpers';
import { OryAuthState } from './ory-auth.models';

export const oryAuthFeatureKey = 'oryAuth';

export const selectOryAuthState =
  createFeatureSelector<OryAuthState>(oryAuthFeatureKey);

export const selectSession = createSelector(
  selectOryAuthState,
  (state) => state.session,
);

export const selectIsAuthenticated = createSelector(
  selectSession,
  (session) => !!session,
);

export const selectSessionLoading = createSelector(
  selectOryAuthState,
  (state) => state.sessionLoading,
);

export const selectSessionError = createSelector(
  selectOryAuthState,
  (state) => state.sessionError,
);

// Current Flow State
export const selectCurrentFlowId = createSelector(
  selectOryAuthState,
  (state) => state.flowId,
);

export const selectCurrentCsrfToken = createSelector(
  selectOryAuthState,
  (state) => state.csrfToken,
);

export const selectRegistrationData = createSelector(
  selectOryAuthState,
  (state) => state.registrationFormData,
);

export const selectLoginData = createSelector(
  selectOryAuthState,
  (state) => state.loginFormData,
);

export const selectLoginFlow = createSelector(
  selectOryAuthState,
  (state) => state.loginFlow,
);

export const selectLoginFlowId = createSelector(
  selectLoginFlow,
  (flow) => flow?.id,
);

export const selectLoginFlowLoading = createSelector(
  selectOryAuthState,
  (state) => state.loginFlowLoading,
);

export const selectLoginFlowError = createSelector(
  selectOryAuthState,
  (state) => state.loginFlowError,
);

export const selectLoginCsrfToken = createSelector(selectLoginFlow, (flow) =>
  getNodeValue(flow?.ui?.nodes, 'csrf_token'),
);

export const selectRegistrationFlow = createSelector(
  selectOryAuthState,
  (state) => state.registrationFlow,
);

export const selectRegistrationFlowId = createSelector(
  selectRegistrationFlow,
  (flow) => flow?.id,
);

export const selectRegistrationFlowLoading = createSelector(
  selectOryAuthState,
  (state) => state.registrationFlowLoading,
);

export const selectRegistrationFlowError = createSelector(
  selectOryAuthState,
  (state) => state.registrationFlowError,
);

export const selectRegistrationCsrfToken = createSelector(
  selectRegistrationFlow,
  (flow) => getNodeValue(flow?.ui?.nodes, 'csrf_token'),
);

export const selectRegistrationPhase = createSelector(
  selectRegistrationFlow,
  (flow) => (hasNode(flow?.ui?.nodes, 'code') ? 'code' : 'email'),
);

export const selectIsSubmitting = createSelector(
  selectOryAuthState,
  (state) => state.isSubmitting,
);

export const selectSubmitError = createSelector(
  selectOryAuthState,
  (state) => state.submitError,
);

export const selectIsResending = createSelector(
  selectOryAuthState,
  (state) => state.isResending,
);

export const selectResendError = createSelector(
  selectOryAuthState,
  (state) => state.resendError,
);

export const selectResendCountdown = createSelector(
  selectOryAuthState,
  (state) => state.resendCountdown,
);

export const selectCanResend = createSelector(
  selectResendCountdown,
  selectIsResending,
  (countdown, isResending) => countdown === 0 && !isResending,
);

export const selectIsLoading = createSelector(
  selectSessionLoading,
  selectLoginFlowLoading,
  selectRegistrationFlowLoading,
  selectIsSubmitting,
  selectIsResending,
  (sessionLoading, loginLoading, registrationLoading, submitting, resending) =>
    sessionLoading ||
    loginLoading ||
    registrationLoading ||
    submitting ||
    resending,
);

export const selectLogoutLoading = createSelector(
  selectOryAuthState,
  (state) => state.logoutLoading,
);

export const selectLogoutError = createSelector(
  selectOryAuthState,
  (state) => state.logoutError,
);

export const selectAnyError = createSelector(
  selectSessionError,
  selectLoginFlowError,
  selectRegistrationFlowError,
  selectSubmitError,
  selectResendError,
  selectLogoutError,
  (
    sessionError,
    loginError,
    registrationError,
    submitError,
    resendError,
    logoutError,
  ) =>
    sessionError ||
    loginError ||
    registrationError ||
    submitError ||
    resendError ||
    logoutError,
);

export const selectCurrentFlow = createSelector(
  selectLoginFlow,
  selectRegistrationFlow,
  (loginFlow, registrationFlow) => loginFlow || registrationFlow,
);

export const selectCurrentFlowType = createSelector(
  selectOryAuthState,
  (state) => state.flowType,
);

export const selectCurrentFlowCsrfToken = createSelector(
  selectCurrentFlow,
  (flow) => getNodeValue(flow?.ui?.nodes, 'csrf_token'),
);

// Redirect parameters
export const selectRedirectTo = createSelector(
  selectOryAuthState,
  (state) => state.redirectTo,
);
